import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SubscriptionAccessService } from '@core/subscription/subscription-access.service';
import { SubscriptionService } from '@core/subscription/subscription.service';
import { SubscriptionQuery } from '@core/subscription/subscription.query';
import { TrialStatus, Subscription } from '@core/models/subscription.model';
import { SubscriptionUpgradeDialogComponent } from '@shared/subscription/subscription-upgrade-dialog/subscription-upgrade-dialog.component';
import { UpdatePaymentMethodDialogComponent } from '@shared/subscription/update-payment-method-dialog/update-payment-method-dialog.component';
import { OrganizationQuery } from '@core/organization/organization.query';
import { AppStoreQuery } from '@core/state/app-state.query';
import { DialogComponent, DialogData, DialogResult } from '@shared/dialog/dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { Observable, interval, of, combineLatest } from 'rxjs';
import { catchError, map, startWith, shareReplay, switchMap, tap, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TypedFormGroup } from '@feature/forms/form-model';

@UntilDestroy()
@Component({
  selector: 'wai-trial-countdown-banner',
  templateUrl: './trial-countdown-banner.component.html',
  styleUrls: ['./trial-countdown-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TrialCountdownBannerComponent implements OnInit {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  public trialStatus$: Observable<TrialStatus | null>;
  public daysRemaining$: Observable<number | null>;
  public showBanner$: Observable<boolean>;
  public currentSubscription$: Observable<Subscription | null>;
  /** The trial started without SEPA details and none are on file (KAP-1897). */
  public paymentMethodRequired$: Observable<boolean>;

  constructor(
    private subscriptionService: SubscriptionService,
    private subscriptionQuery: SubscriptionQuery,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private organizationQuery: OrganizationQuery,
    private appStoreQuery: AppStoreQuery,
    private translate: TranslateService,
    private subscriptionAccessService: SubscriptionAccessService,
  ) {
    const isStaff$ = this.appStoreQuery.selectUser().pipe(map((user) => user?.isStaff === true));

    // Load trial status only for LP users (investor or client organizations) and not staff
    // Try to get from store first, then fallback to service call
    this.trialStatus$ = combineLatest([isStaff$, this.organizationQuery.selectChosenOrganizationType()]).pipe(
      switchMap(([isStaff, orgType]) => {
        // Skip for staff users or non-LP organizations
        if (isStaff || (orgType !== 'investor' && orgType !== 'client')) {
          return of(null);
        }
        // Only fetch trial status for LP organizations (investor or client)
        // First check store for cached trial status
        const cachedTrialStatus = this.subscriptionQuery.getTrialStatus();

        // Check store subscription for trial status as fallback
        const storeSubscription$ = this.subscriptionQuery.selectCurrentSubscription().pipe(
          map((sub) => {
            if (sub?.isTrialPeriod !== undefined) {
              return {
                isTrialPeriod: sub.isTrialPeriod,
                trialEndDate: sub.trialEndDate || null,
                daysRemaining: sub.daysRemainingInTrial || null,
              } as TrialStatus;
            }
            return null;
          }),
        );

        // Only fetch from service if store doesn't have trial status
        const serviceTrialStatus$ = cachedTrialStatus
          ? of(null) // Store has it, don't call service
          : this.subscriptionService.getTrialStatus().pipe(
              catchError((error) => {
                console.error('Error loading trial status:', error);
                return of(null);
              }),
            );

        // Combine all sources: prefer cached trial status, then subscription, then service
        return combineLatest([
          of(cachedTrialStatus), // Start with cached value
          storeSubscription$,
          serviceTrialStatus$,
        ]).pipe(
          map(([cachedStatus, subStatus, serviceStatus]) => {
            // Prefer cached trial status, then subscription status, then service status
            return cachedStatus || subStatus || serviceStatus;
          }),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    // Calculate days remaining with hourly updates. Driven by `trialEndDate`, not
    // `isTrialPeriod`/`daysRemaining` — those require the subscription's `is_trial`
    // flag, which a plain admin edit of `trial_end_date` does not set, so the
    // countdown would read null even with a valid end date (KAP-1854).
    this.daysRemaining$ = this.trialStatus$.pipe(
      switchMap((status) => {
        if (!status?.trialEndDate) {
          return of(null);
        }

        // Update countdown every hour
        return interval(3600000).pipe(
          startWith(0),
          map(() => this.computeDaysLeft(status.trialEndDate)),
        );
      }),
    );

    // Load current subscription - make it reactive to store changes
    // This ensures the banner updates when subscription changes (e.g., after upgrade/cancel)
    const storeSubscription$ = this.subscriptionQuery
      .selectCurrentSubscription()
      .pipe(startWith(this.subscriptionQuery.getCurrentSubscription()));

    // Also fetch from service to ensure we have latest data initially
    const serviceSubscription$ = this.subscriptionService.getCurrentSubscription().pipe(
      catchError((error) => {
        console.error('Error loading subscription:', error);
        return of(null);
      }),
      take(1), // Only fetch once initially
    );

    // Combine both: use service for initial load, then store for reactive updates
    this.currentSubscription$ = combineLatest([serviceSubscription$, storeSubscription$]).pipe(
      map(([serviceSub, storeSub]) => {
        // Prefer store subscription if available (it's reactive to updates)
        // Otherwise use service subscription for initial load
        return storeSub || serviceSub;
      }),
    );

    // Show banner only for standard tier LP users with active trial
    // Similar to how user panel handles trial badge visibility
    // Check both subscription.isTrialPeriod and trialStatus.isTrialPeriod for reliability
    this.showBanner$ = combineLatest([
      this.currentSubscription$,
      this.trialStatus$,
      this.subscriptionAccessService.selectDueDiligencePartnerFeaturesEnabled(),
    ]).pipe(
      map(([subscription, trialStatus, showDdPartnerUi]) => {
        if (!showDdPartnerUi) {
          return false;
        }
        // Show whenever an unexpired trial end date is present, driven by
        // `trialEndDate` rather than the `is_trial`-gated flags (KAP-1854 — see
        // daysRemaining$ above). Every paid tier can trial as of KAP-1853.
        const end = trialStatus?.trialEndDate ?? subscription?.trialEndDate ?? null;
        return this.computeDaysLeft(end) !== null;
      }),
    );

    this.paymentMethodRequired$ = this.currentSubscription$.pipe(
      map((subscription) => subscription?.paymentMethodRequired === true),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  ngOnInit(): void {
    // Trial status is now reactive to store updates
    // Store is updated when subscription changes or after upgrade/cancel
    // No periodic refresh needed - component observables handle reactivity
    this.cdr.markForCheck();
  }

  /**
   * Whole days from today until `trialEndDate`, or null when absent/invalid/past.
   * Date-only comparison, matching the backend's `trial_end_date >= today` /
   * `(trial_end_date - today).days` (KAP-1854).
   */
  private computeDaysLeft(trialEndDate: string | null | undefined): number | null {
    if (!trialEndDate) {
      return null;
    }
    const endDate = new Date(trialEndDate);
    if (isNaN(endDate.getTime())) {
      return null;
    }
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfEnd = new Date(endDate);
    startOfEnd.setHours(0, 0, 0, 0);
    const days = Math.round((startOfEnd.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24));
    return days >= 0 ? days : null;
  }

  /**
   * Collect the SEPA details a trial started without (KAP-1897). The backend
   * attaches the new mandate to the trialing subscription, and the dialog refreshes
   * the subscription in the store, which clears `paymentMethodRequired$`.
   */
  public onAddBankDetails(): void {
    this.dialog
      .open(UpdatePaymentMethodDialogComponent, {
        width: '640px',
        maxWidth: '95vw',
        disableClose: true,
      })
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(() => this.cdr.markForCheck());
  }

  public onUpgrade(): void {
    this.subscriptionAccessService
      .selectDueDiligencePartnerFeaturesEnabled()
      .pipe(
        take(1),
        switchMap((enabled) => {
          if (!enabled) {
            return of(null);
          }
          return combineLatest([
            this.currentSubscription$,
            this.subscriptionQuery.selectAvailableTiers('lp').pipe(
              switchMap((tiers) => {
                if (tiers && tiers.length > 0) {
                  return of(tiers);
                }
                return this.subscriptionService.getAvailableTiers('lp');
              }),
            ),
          ]).pipe(take(1));
        }),
        untilDestroyed(this),
        tap((tuple) => {
          if (!tuple) {
            return;
          }
          const [subscription, tiers] = tuple;
          // Find professional tier as default when coming from trial
          const professionalTier = tiers.find((t) => t.code === 'lp_professional');
          const defaultTierUid = professionalTier?.uid || null;

          const dialogRef = this.dialog.open(SubscriptionUpgradeDialogComponent, {
            width: '900px',
            maxWidth: '95vw',
            data: {
              currentSubscription: subscription,
              organizationType: 'lp',
              defaultTierUid: defaultTierUid,
            },
            disableClose: true,
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              // Refresh subscription and trial status after upgrade (only for LP users).
              // The tier check that used to sit here branched on `lp_standard` but
              // called getTrialStatus(true) either way, so it only differed in whether
              // the result was mapped to null. Any tier can trial now (KAP-1853), so
              // the branch is gone.
              this.organizationQuery
                .selectChosenOrganizationType()
                .pipe(
                  switchMap((orgType) => {
                    if (orgType === 'investor' || orgType === 'client') {
                      // Refresh subscription first to get updated tier
                      return this.subscriptionService
                        .getCurrentSubscription(true)
                        .pipe(switchMap(() => this.subscriptionService.getTrialStatus(true)));
                    }
                    return of(null);
                  }),
                  untilDestroyed(this),
                )
                .subscribe(() => {
                  this.cdr.markForCheck();
                });
            }
          });
        }),
      )
      .subscribe();
  }


  public onCancelTrial(): void {
    // Show confirmation dialog before cancelling trial
    const confirmDialogData: DialogData = {
      title: this.translate.instant('INTL_CONFIRM_DELETE') || 'Confirm Cancel Trial',
      text: 'Are you sure you want to cancel the trial period?',
      confirmButton: {
        label: this.translate.instant('INTL_YES'),
        color: 'warn',
        icon: 'cancel',
      },
      dismissButton: {
        label: this.translate.instant('INTL_CANCEL'),
        icon: 'close',
      },
    };

    const confirmDialogRef = this.dialog.open<DialogComponent, DialogData, DialogResult>(DialogComponent, {
      data: confirmDialogData,
      width: '500px',
      disableClose: true,
    });

    confirmDialogRef
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        switchMap((confirmed) => {
          if (!confirmed) {
            // User cancelled the confirmation dialog
            return of(null);
          }

          // User confirmed, proceed with trial cancellation
          return this.subscriptionService.cancelTrial().pipe(
            switchMap(() => {
              // Refresh subscription in store (already done by service)
              // Refresh trial status after cancellation (only for LP users). The
              // `lp_standard` branch that used to be here called getTrialStatus(true)
              // on both sides; any tier can trial now (KAP-1853).
              return this.organizationQuery.selectChosenOrganizationType().pipe(
                switchMap((orgType) => {
                  if (orgType === 'investor' || orgType === 'client') {
                    // Refresh subscription to get updated tier
                    return this.subscriptionService
                      .getCurrentSubscription(true)
                      .pipe(switchMap(() => this.subscriptionService.getTrialStatus(true)));
                  }
                  return of(null);
                }),
              );
            }),
            tap(() => {
              // Force change detection to update the UI
              this.cdr.markForCheck();
            }),
            catchError(() => {
              // Even on error, try to refresh to ensure UI is in sync
              this.organizationQuery
                .selectChosenOrganizationType()
                .pipe(
                  switchMap((orgType) => {
                    if (orgType === 'investor' || orgType === 'client') {
                      return this.subscriptionService.getTrialStatus(true).pipe(catchError(() => of(null)));
                    }
                    return of(null);
                  }),
                  untilDestroyed(this),
                )
                .subscribe(() => {
                  this.cdr.markForCheck();
                });
              return of(null);
            }),
          );
        }),
      )
      .subscribe();
  }
}
